import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

interface Tag {
  id: string;
  tag: string;
  createdBy: string;
}

const SUGGESTIONS = [
  'vip',
  'at-risk',
  'renewal-soon',
  'needs-rfp',
  'new-account',
  'pilot',
  'beta',
  'sensitive',
  'champion',
  'price-sensitive',
];

export function TagsPanel({ customerId }: { customerId: string }) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ tags: Tag[] }>(
        `/api/tags?customer_id=${encodeURIComponent(customerId)}`,
      );
      setTags(data.tags);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const addTag = async (raw: string) => {
    const tag = raw.trim().toLowerCase();
    if (!tag) return;
    if (tags.some((t) => t.tag === tag)) {
      setDraft('');
      return;
    }
    setPosting(true);
    setError(null);
    try {
      await apiFetch('/api/tags', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, tag }),
      });
      setDraft('');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPosting(false);
    }
  };

  const removeTag = async (id: string) => {
    const prev = tags;
    setTags(tags.filter((t) => t.id !== id));
    try {
      await apiFetch(`/api/tags/${id}`, { method: 'DELETE' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setTags(prev);
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <span className="section-label">Tags</span>
        <span className="section-meta">{tags.length} on this customer</span>
      </div>
      <div className="section-body">
        {error && <div className="banner banner-error">{error}</div>}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginBottom: 12,
          }}
        >
          {loading && tags.length === 0 && (
            <span
              style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}
            >
              Loading…
            </span>
          )}
          {!loading && tags.length === 0 && (
            <span
              style={{
                fontSize: 'var(--fs-base)',
                color: 'var(--text-muted)',
                fontStyle: 'italic',
              }}
            >
              No tags yet — add one below.
            </span>
          )}
          {tags.map((t) => (
            <span
              key={t.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 6px 4px 10px',
                background: 'var(--accent-light)',
                color: 'var(--accent-darker)',
                border: '1px solid var(--accent-soft)',
                borderRadius: 'var(--radius-pill)',
                fontSize: 'var(--fs-sm)',
                fontWeight: 600,
                letterSpacing: '0.005em',
              }}
            >
              {t.tag}
              <button
                onClick={() => removeTag(t.id)}
                aria-label={`Remove ${t.tag}`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--accent-dark)',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 'var(--fs-md)',
                  lineHeight: 1,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'inherit',
                }}
                title="Remove tag"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag(draft);
              }
            }}
            placeholder="Add a tag (lowercase, no spaces — e.g. needs-rfp)"
            className="input"
            style={{ maxWidth: 320 }}
            disabled={posting}
          />
          <button
            className="btn btn-primary"
            onClick={() => addTag(draft)}
            disabled={posting || !draft.trim()}
          >
            Add
          </button>
        </div>
        <div
          style={{
            marginTop: 10,
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
          }}
        >
          Suggestions:{' '}
          {SUGGESTIONS.filter((s) => !tags.some((t) => t.tag === s))
            .slice(0, 8)
            .map((s) => (
              <button
                key={s}
                onClick={() => addTag(s)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: 'var(--fs-sm)',
                  fontFamily: 'inherit',
                }}
              >
                {s}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
